import React, {memo, ReactNode} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link} from 'react-router';
import styled from 'styled-components';
import uniqolor from 'uniqolor';

import {LazyTooltip} from 'app/components/general/LazyTooltip';

import type {OverlayPlacementType} from 'app/general/overlays';

interface Props {
  title: string;
  border?: boolean;
  link?: string;
  children?: ReactNode | null;
  backgroundImage?: string;
  image?: string | null;
  displayBlock?: boolean;
  multiple?: boolean;
  className?: string;
  style?: any;
  tooltipPlacement?: OverlayPlacementType;
  tooltipContent?: string | ReactNode;
  circle?: boolean;
  small?: boolean;
}
export const BaseAvatar = memo<Props>(function BaseAvatar({
  title = '',
  backgroundImage,
  image,
  children = null,
  className = '',
  multiple,
  style,
  displayBlock,
  tooltipPlacement,
  tooltipContent,
  link,
  circle,
  small,
}: Props) {
  const initials = (title || '?')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((n) => n[0].toUpperCase())
    .join('');

  const ContainerElement =
    multiple === true ? MultipleStyledBoxDIV : SingleStyledBoxDIV;

  const elementContent = (
    <LazyTooltip
      hide={tooltipContent == null || multiple === true}
      className={className}
      placement={tooltipPlacement}
      tooltip={tooltipContent}
    >
      <ContainerElement
        charsCount={initials.length}
        displayBlock={displayBlock}
        style={{...style, backgroundColor: colorForString(title)}}
        className={cs(
          'avatar',
          'border-avatar',
          multiple === true ? '' : className,
          {circle, small}
        )}
      >
        {backgroundImage != null ? (
          <div
            className="background-image"
            style={{backgroundImage: `url("${backgroundImage}")`}}
          />
        ) : null}
        <div className="name">{initials}</div>
        {image != null ? (
          <div className="image" style={{backgroundImage: `url("${image}")`}} />
        ) : null}
        {children}
      </ContainerElement>
    </LazyTooltip>
  );

  return link == null ? (
    elementContent
  ) : (
    <Link to={link}>{elementContent}</Link>
  );
});

const colorForString = (title: string) =>
  uniqolor(title, {
    saturation: 50,
    lightness: 65,
  }).color;

const BaseStyledBoxDIV = styled.div<{
  charsCount: number;
  displayBlock?: boolean;
}>`
  position: relative;
  overflow: hidden;
  font-size: 1rem;
  color: #FFF;
  text-align: center;
  background-color: ${colors.mediumGray};

  > .name, > .image, > .background-image {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
  }

  > .name {
    font-size: ${({charsCount}) =>
      `${Math.max(1.4 - charsCount * 0.2, 0.5)}em`};
    line-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > .image, > .background-image {
    background-position: center;
    background-size: cover;
  }

  > .background-image {
    opacity: 0.25;
  }

  > .status {
    position: absolute;
    bottom: 3px;
    left: 3px;
    line-height: 1em;
    font-size: 10pt;
  }
}`;

export const SingleStyledBoxDIV = styled(BaseStyledBoxDIV)<{
  displayBlock?: boolean;
}>`
  display: ${({displayBlock}) =>
    displayBlock === true ? 'block' : 'inline-block'};
  width: 2.2em;
  min-width: 2.2em;
  height: 2.2em;
  border-radius: 4px;

  &.circle {
    border-radius: 50%;
  }

  &.small {
    font-size: 0.75rem;
  }

  &.border-avatar {
    border: 2px solid #fff;
    box-shadow: 0 0 3px 0px rgba(0, 0, 0, 0.25);

    > .name {
      line-height: inherit;
    }
  }
`;

const MultipleStyledBoxDIV = styled(BaseStyledBoxDIV)`
  display: block;
  width: 100%;
  border-radius: 0;
  flex-basis: 100%;
`;
